import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Server, Network, Shield, Wrench, Cloud, Users } from 'lucide-react';

const highlights = [
  {
    icon: Server,
    title: 'Enterprise Infrastructure',
    description: 'Supporting enterprise IT infrastructure across large-scale Amazon fulfillment and robotics facilities'
  },
  {
    icon: Network,
    title: 'Network Operations',
    description: 'Working with Cisco and Juniper networking equipment, VLANs, and network segmentation'
  },
  {
    icon: Shield,
    title: 'Deployment Engineering',
    description: 'Facility builds, expansions, and retrofits with infrastructure deployments'
  },
  {
    icon: Wrench,
    title: 'Troubleshooting',
    description: 'Methodical troubleshooting of complex IT issues while minimizing operational impact'
  },
  {
    icon: Cloud,
    title: 'Server & Virtualization',
    description: 'Windows and Linux servers, VMware ESXi, vSphere, vCenter, and Hyper-V exposure'
  },
  {
    icon: Users,
    title: 'Team Collaboration',
    description: 'Creating SOPs, mentoring junior technicians, and cross-functional collaboration'
  }
];

export const ProfessionalOverview = () => {
  return (
    <section id="about" className="py-20 md:py-28 bg-background">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Professional Overview
          </h2>
          <div className="w-20 h-1 bg-primary mx-auto rounded-full mb-6" />
          <p className="text-muted-foreground max-w-3xl mx-auto text-base sm:text-lg leading-relaxed">
            A hands-on IT Support Engineer with experience supporting enterprise infrastructure, 
            network operations, and large-scale deployment projects across production environments.
          </p>
        </div>

        {/* Highlight Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {highlights.map((item, index) => (
            <Card 
              key={index} 
              className="group bg-card border-border hover:border-primary/30 card-hover cursor-default"
            >
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-primary-soft flex items-center justify-center mb-4 group-hover:bg-primary/15 transition-colors">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {item.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional context */}
        <div className="mt-12 md:mt-16 p-6 md:p-8 bg-secondary/50 rounded-2xl border border-border">
          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="flex-1">
              <h3 className="font-heading font-semibold text-lg text-foreground mb-3">
                Current Focus
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Currently supporting Amazon fulfillment and robotics facilities, working on infrastructure 
                deployments, expansions, and retrofits. Work spans network equipment, servers, structured 
                cabling, hardware installation, and operational troubleshooting in highly available, 
                production-critical environments.
              </p>
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-semibold text-lg text-foreground mb-3">
                Technical Background
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Experience supporting PLC-connected conveyance and control systems, along with 
                foundational PLC Ladder Logic knowledge gained through hands-on exposure and ongoing 
                training. Background includes working with virtualization platforms with foundational 
                hands-on exposure.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProfessionalOverview;
